import { createBlockId, readBlockId } from "@repo/shared";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Extension } from "@tiptap/react";
import { BLOCK_ID_NODE_TYPES, isBlockIdNodeType } from "../block-id-node-types";

const BLOCK_ID_ATTR = "blockId";
const BLOCK_ID_DATA_ATTR = "data-block-id";

function assignMissingBlockIds(
  doc: PMNode,
  tr: Transaction
): { tr: Transaction; hasChanges: boolean } {
  let nextTr = tr;
  const seen = new Set<string>();
  let hasChanges = false;

  doc.descendants((node, pos) => {
    if (!isBlockIdNodeType(node.type.name)) return;

    const currentId = readBlockId(node.attrs?.[BLOCK_ID_ATTR]);
    if (currentId && !seen.has(currentId)) {
      seen.add(currentId);
      return;
    }

    const nextId = createBlockId();
    seen.add(nextId);
    hasChanges = true;
    nextTr = nextTr.setNodeMarkup(
      pos,
      undefined,
      {
        ...node.attrs,
        [BLOCK_ID_ATTR]: nextId,
      },
      node.marks
    );
  });

  return { tr: nextTr, hasChanges };
}

/**
 * Assign stable IDs to block nodes used by block-level clarification anchors.
 *
 * The IDs are persisted in node attrs and serialized as `data-block-id`.
 * Existing IDs are preserved; only missing/duplicate IDs are rewritten.
 */
export const BlockIds = Extension.create({
  name: "blockIds",

  onCreate() {
    const { state, view } = this.editor;
    const { tr, hasChanges } = assignMissingBlockIds(state.doc, state.tr);
    if (hasChanges) {
      view.dispatch(tr);
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: [...BLOCK_ID_NODE_TYPES],
        attributes: {
          [BLOCK_ID_ATTR]: {
            default: null,
            parseHTML: (element) => element.getAttribute(BLOCK_ID_DATA_ATTR),
            renderHTML: (attributes: Record<string, unknown>) => {
              const blockId = readBlockId(attributes[BLOCK_ID_ATTR]);
              return blockId ? { [BLOCK_ID_DATA_ATTR]: blockId } : {};
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("blockIds"),
        appendTransaction: (_transactions, _oldState, newState) => {
          const { tr, hasChanges } = assignMissingBlockIds(newState.doc, newState.tr);
          return hasChanges ? tr : null;
        },
      }),
    ];
  },
});
